/**
 * Faltbare Bereiche im Markdown-Quelltext.
 *
 * Rein textbasiert (keine DOM-, keine CodeMirror-Abhängigkeit), damit Code-
 * Seite (CodeMirror-Faltung) und Vorschau (Dreiecke am Rand) garantiert
 * dieselben Bereiche meinen. Der Text selbst wird nie verändert – Falten ist
 * ausschließlich Anzeige, in der Datenbank steht immer alles.
 *
 * Vier Sorten:
 *   - `heading`   `## Titel` bis kurz vor die nächste gleich- oder höherrangige
 *                 Überschrift.
 *   - `list`      Listenpunkt mit tiefer eingerückten Folgezeilen.
 *   - `container` `::: info … :::` – **einschließlich** der Schlusszeile, sonst
 *                 würde beim Zuklappen Text von hinter dem Block in die
 *                 gefaltete Stelle rutschen.
 *   - `fence`     Codeblock ```` ```java … ``` ```` (auch Mermaid/TikZ) –
 *                 ebenfalls einschließlich der Schlusszeile.
 *   - `math`      `$$ … $$`-Formelblock (öffnende/schließende Zeile jeweils
 *                 allein für sich) – einschließlich beider `$$`-Zeilen.
 *
 * Überschriften, Listen und Formeln lassen sich auf beiden Seiten falten,
 * Container und Codeblöcke nur im Quelltext: in der Vorschau sind sie schon
 * fertige Boxen bzw. Diagramme, dort soll nichts automatisch verschwinden.
 * Eine Formel ist dagegen oft eine lange, mehrzeilige Rechnung (siehe
 * mathAssist.ts) – die soll man auch dort wegklappen können.
 */

export type FoldKind = "heading" | "list" | "container" | "fence" | "math";

export interface FoldRange {
  kind: FoldKind;
  /** 0-basierte Zeile mit der Überschrift / dem Listenpunkt / dem `:::`. */
  from: number;
  /** 0-basierte letzte Zeile, die mit eingeklappt wird (einschließlich). */
  to: number;
}

const HEADING = /^(#{1,6})[ \t]+\S/;
const BULLET = /^([ \t]*)(?:[-*+]|\d+[.)])[ \t]+/;
const FENCE = /^[ \t]*(```+|~~~+)/;
const OPEN = /^[ \t]*:::+[ \t]*\S/;
const CLOSE = /^[ \t]*:::+[ \t]*$/;
const MATH_DELIM = /^[ \t]*\$\$[ \t]*$/;
/** So viele Leerzeilen am Stück darf ein Überschriften-Abschnitt enthalten. */
const MAX_BLANK_RUN = 3;

/** Einrücktiefe in Spalten (Tab zählt wie zwei Leerzeichen). */
function indentOf(line: string): number {
  let n = 0;
  for (const ch of line) {
    if (ch === " ") n += 1;
    else if (ch === "\t") n += 2;
    else break;
  }
  return n;
}

export interface FenceScan {
  /** Pro Zeile: gehört sie zu einem Codeblock (Zäune eingeschlossen)? */
  inCode: boolean[];
  /** Zaunpaare als [Startzeile, Schlusszeile]. */
  blocks: [number, number][];
}

/**
 * Findet alle Codeblöcke (``` / ~~~). Deren Inhalt ist für die
 * Strukturerkennung tabu – ein `:::` oder `# ` im Beispielcode ist keine
 * Struktur, und ein `*` darin keine Auszeichnung.
 */
export function scanFences(lines: string[]): FenceScan {
  const inCode = new Array<boolean>(lines.length).fill(false);
  const blocks: [number, number][] = [];
  let fence: string | null = null;
  let start = 0;
  for (let i = 0; i < lines.length; i++) {
    const m = FENCE.exec(lines[i]);
    if (fence) {
      inCode[i] = true;
      if (m && m[1][0] === fence[0] && m[1].length >= fence.length) {
        blocks.push([start, i]);
        fence = null;
      }
    } else if (m) {
      fence = m[1];
      start = i;
      inCode[i] = true;
    }
  }
  // Unabgeschlossener Zaun: bis zum Dokumentende.
  if (fence) blocks.push([start, lines.length - 1]);
  return { inCode, blocks };
}

/** Alle faltbaren Bereiche eines Dokuments, aufsteigend nach Startzeile. */
export function foldRanges(lines: string[]): FoldRange[] {
  const out: FoldRange[] = [];
  const { inCode, blocks } = scanFences(lines);
  for (const [from, to] of blocks) {
    if (to > from) out.push({ kind: "fence", from, to });
  }

  // --- Überschriften ---
  const heads: { line: number; level: number }[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (inCode[i]) continue;
    const m = HEADING.exec(lines[i]);
    if (m) heads.push({ line: i, level: m[1].length });
  }
  heads.forEach((h, idx) => {
    let end = lines.length - 1;
    for (let j = idx + 1; j < heads.length; j++) {
      if (heads[j].level <= h.level) {
        end = heads[j].line - 1;
        break;
      }
    }
    // Ein großer Abstand trennt: mehr als drei Leerzeilen am Stück beenden den
    // Abschnitt. Was danach kommt, gehört erkennbar nicht mehr dazu und wird
    // beim Zuklappen der Überschrift nicht mit verschluckt.
    let blanks = 0;
    for (let j = h.line + 1; j <= end; j++) {
      if (lines[j].trim()) {
        blanks = 0;
        continue;
      }
      if (++blanks > MAX_BLANK_RUN) {
        end = j - blanks;
        break;
      }
    }
    while (end > h.line && !lines[end].trim()) end--; // Leerzeilen nicht mitnehmen
    if (end > h.line) out.push({ kind: "heading", from: h.line, to: end });
  });

  // --- Listenpunkte mit Unterpunkten ---
  for (let i = 0; i < lines.length; i++) {
    if (inCode[i] || !BULLET.test(lines[i])) continue;
    const base = indentOf(lines[i]);
    let last = i;
    for (let j = i + 1; j < lines.length; j++) {
      if (!lines[j].trim()) continue; // Leerzeile: erst die nächste Zeile entscheidet
      if (indentOf(lines[j]) > base) last = j;
      else break;
    }
    if (last > i) out.push({ kind: "list", from: i, to: last });
  }

  // --- ::: Container (Schlusszeile gehört dazu) ---
  const stack: number[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (inCode[i]) continue;
    if (OPEN.test(lines[i])) stack.push(i);
    else if (CLOSE.test(lines[i])) {
      const from = stack.pop();
      if (from != null && i > from) out.push({ kind: "container", from, to: i });
    }
  }

  // --- $$ Formelblöcke (öffnende/schließende Zeile je allein, kein Verschachteln) ---
  let mathOpen = -1;
  for (let i = 0; i < lines.length; i++) {
    if (inCode[i] || !MATH_DELIM.test(lines[i])) continue;
    if (mathOpen === -1) {
      mathOpen = i;
    } else {
      if (i > mathOpen) out.push({ kind: "math", from: mathOpen, to: i });
      mathOpen = -1;
    }
  }

  out.sort((a, b) => a.from - b.from || a.to - b.to);
  return out;
}

/**
 * Faltbarer Bereich, der genau an `line` beginnt. Beginnen mehrere dort
 * (z. B. Überschrift, die zugleich Listenpunkt wäre), gewinnt der längste.
 */
export function foldRangeAt(lines: string[], line: number): FoldRange | null {
  let best: FoldRange | null = null;
  for (const r of foldRanges(lines)) {
    if (r.from !== line) continue;
    if (!best || r.to > best.to) best = r;
  }
  return best;
}
